/*
    Calculates the late fee applied based on a company's fee settings
*/

import { clamp, isEmpty, omit, round, subtract } from 'lodash';

import {
  AppPaymentDueFragment,
  CompanyLateFeeSettingsFixed as GqlCompanyLateFeeSettingsFixed,
  CompanyLateFeeSettingsPercent as GqlCompanyLateFeeSettingsPercent,
  CompanyLateFeeType as GqlCompanyLateFeeType,
  LoanLateFee as GqlLoanLateFee,
  LoanLateFeeType as GqlLoanLateFeeType,
} from '@willow/graphql-iso/src/app';
import {
  CompanyLateFeeSettings,
  CompanyLateFeeSettingsFixed,
  CompanyLateFeeSettingsPercent,
  LoanLateFee,
  LoanLateFeeFixed,
  LoanLateFeePercent,
  PaymentDue,
} from '@willow/types-iso';

import { multiply } from '../utils/math';
import { percentageToDecimal } from '../utils/percentageToDecimal';

const calculatePercentFee = ({
  amountToApplyFee,
  decimalCharge,
  minAmount,
  maxAmount,
}: {
  amountToApplyFee: number;
  decimalCharge: number;
  minAmount: number | undefined;
  maxAmount: number | undefined;
}): number => {
  const baseOverrideAmount = multiply(decimalCharge, amountToApplyFee);
  const boundedOverrideAmount = clamp(baseOverrideAmount, minAmount ?? -Infinity, maxAmount ?? Infinity);
  return round(boundedOverrideAmount, 2);
};

export function getLateFeeAmount(
  companyFeeSettings: CompanyLateFeeSettings,
  paymentDue: PaymentDue,
  loanOverrides: LoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number;
export function getLateFeeAmount(
  companyFeeSettings: GqlCompanyLateFeeSettingsFixed | GqlCompanyLateFeeSettingsPercent,
  paymentDue: AppPaymentDueFragment,
  loanOverrides: GqlLoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number;
export function getLateFeeAmount(
  companyFeeSettings: CompanyLateFeeSettings | GqlCompanyLateFeeSettingsFixed | GqlCompanyLateFeeSettingsPercent,
  paymentDue: PaymentDue | AppPaymentDueFragment,
  loanOverrides: LoanLateFee | GqlLoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number {
  // The fee is calculated on the statement P&I amount
  const { enabled, appliedToAllLoans } = companyFeeSettings;
  if (!enabled) return 0;

  const hasLoanOverride = loanOverrides && !isEmpty(omit(loanOverrides, '__typename'));
  if (!appliedToAllLoans && !hasLoanOverride) return 0;

  // loan stop setting
  if (loanLateFeeStopSettingEnabled) return 0;

  /*
    Determine the amount to apply the fee from
    * We use the payment due total so that draws are included in the fee calculation
    * Partial payments of this payment due are not considered
  */
  const amountToApplyFee = subtract(paymentDue.total, paymentDue.escrow);

  // Maybe apply loan-level overrides
  if (hasLoanOverride) {
    switch (loanOverrides.type) {
      case 'percent':
      case GqlLoanLateFeeType.Percent: {
        const percentOverride = loanOverrides as LoanLateFeePercent;
        return calculatePercentFee({
          amountToApplyFee,
          decimalCharge: percentOverride.decimalCharge,
          minAmount: percentOverride.minAmount,
          maxAmount: percentOverride.maxAmount,
        });
      }
      case 'fixed':
      case GqlLoanLateFeeType.Fixed: {
        const fixedOverride = loanOverrides as LoanLateFeeFixed;
        return fixedOverride.amount;
      }
      default:
        return 0;
    }
  }

  // Otherwise, return the company-level fee
  switch (companyFeeSettings.type) {
    case 'percent':
    case GqlCompanyLateFeeType.Percent: {
      const percentOverride = companyFeeSettings as CompanyLateFeeSettingsPercent;
      return calculatePercentFee({
        amountToApplyFee,
        decimalCharge: percentageToDecimal(percentOverride.percentCharge),
        minAmount: percentOverride.minAmount,
        maxAmount: percentOverride.maxAmount,
      });
    }
    case 'fixed':
    case GqlCompanyLateFeeType.Fixed: {
      const fixedOverride = companyFeeSettings as CompanyLateFeeSettingsFixed;
      return fixedOverride.amount;
    }
    default:
      return 0;
  }
}
